/**
 * 创建 App 弹窗
 */

import { useState } from 'react'
import { Loader2, Building2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { useToast } from '@/shared/hooks/useToast'
import { AppApiService } from '../services/app-api'
import type { AppListItem, CreateAppRequest, AppPricing } from '../types/app'

interface AppCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (app: AppListItem) => void
}

// 默认套餐价格配置
const defaultPricing: AppPricing = {
  free: { name: '免费版', monthly: 0, yearly: 0 },
  tier1: { name: '基础版', monthly: 990, yearly: 9900 },
  tier2: { name: '高级版', monthly: 2990, yearly: 29900 },
}

export default function AppCreateDialog({
  open,
  onOpenChange,
  onSuccess,
}: AppCreateDialogProps) {
  const { showSuccess, showError } = useToast()

  // 表单状态
  const [formData, setFormData] = useState({
    app_id: '',
    app_name: '',
    company: '',
    description: '',
    sms_sign_name: '',
    sms_template_verification: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 验证表单
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.app_id.trim()) {
      newErrors.app_id = 'App ID 不能为空'
    } else if (!/^[a-z][a-z0-9_]*$/.test(formData.app_id)) {
      newErrors.app_id = 'App ID 必须以小写字母开头，只能包含小写字母、数字和下划线'
    }

    if (!formData.app_name.trim()) {
      newErrors.app_name = 'App 名称不能为空'
    }

    if (!formData.sms_sign_name.trim()) {
      newErrors.sms_sign_name = '短信签名不能为空'
    }

    if (!formData.sms_template_verification.trim()) {
      newErrors.sms_template_verification = '短信模板 ID 不能为空'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 提交创建
  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const request: CreateAppRequest = {
        app_id: formData.app_id.trim(),
        app_name: formData.app_name.trim(),
        company: formData.company.trim() || undefined,
        description: formData.description.trim() || undefined,
        pricing: defaultPricing,
        trial_type: 'count',
        trial_config: { default_count: 10 },
        sms_sign_name: formData.sms_sign_name.trim(),
        sms_template_verification: formData.sms_template_verification.trim(),
      }

      const response = await AppApiService.createApp(request)
      if (response.success && response.data) {
        showSuccess(`App "${response.data.app_name}" 创建成功`)
        
        // 构造 AppListItem 返回
        const newApp: AppListItem = {
          app_id: response.data.app_id,
          app_name: response.data.app_name,
          company: response.data.company || '',
          description: response.data.description || '',
          icon_url: response.data.icon_url,
          created_at: response.data.created_at,
          stats_summary: {
            total_users: 0,
            total_subscribers: 0,
            published_projects: 0,
          },
        }
        
        // 重置表单
        setFormData({
          app_id: '',
          app_name: '',
          company: '',
          description: '',
          sms_sign_name: '',
          sms_template_verification: '',
        })
        setErrors({})
        
        onSuccess(newApp)
      }
    } catch (error: any) {
      console.error('❌ [AppCreateDialog] 创建 App 失败:', error)
      
      // 处理特定错误
      if (error.response?.data?.error?.code === 'APP_EXISTS') {
        setErrors({ app_id: 'App ID 已存在' })
      } else {
        showError('创建 App 失败，请稍后重试')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // 关闭时重置
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({
        app_id: '',
        app_name: '',
        company: '',
        description: '',
        sms_sign_name: '',
        sms_template_verification: '',
      })
      setErrors({})
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>创建新 App</DialogTitle>
          <DialogDescription>
            填写 App 基本信息。创建后可以在详情页配置套餐和试用策略。
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* App ID */}
          <div className="grid gap-2">
            <Label htmlFor="app_id">
              App ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="app_id"
              placeholder="例如：by_your_side"
              value={formData.app_id}
              onChange={(e) =>
                setFormData({ ...formData, app_id: e.target.value.toLowerCase() })
              }
              className={errors.app_id ? 'border-red-500' : ''}
            />
            {errors.app_id ? (
              <p className="text-xs text-red-500">{errors.app_id}</p>
            ) : (
              <p className="text-xs text-slate-500">
                小写字母开头，只能包含小写字母、数字和下划线
              </p>
            )}
          </div>

          {/* App 名称 */}
          <div className="grid gap-2">
            <Label htmlFor="app_name">
              App 名称 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="app_name"
              placeholder="例如：在身边"
              value={formData.app_name}
              onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
              className={errors.app_name ? 'border-red-500' : ''}
            />
            {errors.app_name && (
              <p className="text-xs text-red-500">{errors.app_name}</p>
            )}
          </div>

          {/* 公司 */}
          <div className="grid gap-2">
            <Label htmlFor="company">公司/团队名称</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="company"
                placeholder="例如：示例科技有限公司"
                className="pl-10"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
          </div>

          {/* 描述 */}
          <div className="grid gap-2">
            <Label htmlFor="description">描述</Label>
            <Textarea
              id="description"
              placeholder="简要描述这个 App 的功能和定位..."
              className="resize-none"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* 短信签名 */}
          <div className="grid gap-2">
            <Label htmlFor="sms_sign_name">
              短信签名 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sms_sign_name"
              placeholder="例如：在身边"
              value={formData.sms_sign_name}
              onChange={(e) => setFormData({ ...formData, sms_sign_name: e.target.value })}
              className={errors.sms_sign_name ? 'border-red-500' : ''}
            />
            {errors.sms_sign_name ? (
              <p className="text-xs text-red-500">{errors.sms_sign_name}</p>
            ) : (
              <p className="text-xs text-slate-500">
                用于发送验证码短信，需要在短信平台注册
              </p>
            )}
          </div>

          {/* 短信模板 ID */}
          <div className="grid gap-2">
            <Label htmlFor="sms_template_verification">
              验证码模板 ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sms_template_verification"
              placeholder="例如：SMS_123456789"
              value={formData.sms_template_verification}
              onChange={(e) =>
                setFormData({ ...formData, sms_template_verification: e.target.value })
              }
              className={errors.sms_template_verification ? 'border-red-500' : ''}
            />
            {errors.sms_template_verification && (
              <p className="text-xs text-red-500">{errors.sms_template_verification}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                创建中...
              </>
            ) : (
              '创建 App'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
